import type { AdminGalleryPhoto } from "@tih/shared";
import { ApiError } from "./client";

export type GalleryUploadClientPhase = "uploading" | "extracting" | "processing" | "complete" | "error";

export type GalleryUploadProgress =
  | { phase: "uploading"; percent: number; fileName: string }
  | { phase: "extracting"; fileName: string }
  | { phase: "processing"; current: number; total: number; fileName: string; skipped?: number }
  | {
      phase: "complete";
      fileName: string;
      total: number;
      skipped: number;
      photoCount: number;
      photos: AdminGalleryPhoto[];
    }
  | { phase: "error"; fileName: string; error: string };

type StreamEvent = {
  phase: GalleryUploadClientPhase;
  total?: number;
  current?: number;
  skipped?: number;
  photoCount?: number;
  ok?: boolean;
  error?: string;
  data?: AdminGalleryPhoto[];
};

function parseNdjsonChunk(
  buffer: string,
  onEvent: (event: StreamEvent) => void,
): string {
  const lines = buffer.split("\n");
  const remainder = lines.pop() ?? "";
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    try {
      onEvent(JSON.parse(trimmed) as StreamEvent);
    } catch {
      // ignore partial parse failures
    }
  }
  return remainder;
}

function xhrUploadWithStream(
  url: string,
  formData: FormData,
  fileName: string,
  onProgress: (event: GalleryUploadProgress) => void,
): Promise<GalleryUploadProgress & { phase: "complete" }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Accept", "application/x-ndjson");

    let responseOffset = 0;
    let lineRemainder = "";
    let lastUploadPercent = 0;
    let settled = false;

    const finishComplete = (ev: StreamEvent) => {
      if (settled || ev.phase !== "complete" || ev.ok === false) return;
      settled = true;
      const complete: GalleryUploadProgress & { phase: "complete" } = {
        phase: "complete",
        fileName,
        total: ev.total ?? ev.photoCount ?? 0,
        skipped: ev.skipped ?? 0,
        photoCount: ev.photoCount ?? ev.data?.length ?? 0,
        photos: ev.data ?? [],
      };
      onProgress(complete);
      resolve(complete);
    };

    const finishError = (message: string) => {
      if (settled) return;
      settled = true;
      onProgress({ phase: "error", fileName, error: message });
      reject(new ApiError(message, 0));
    };

    const handleStreamEvent = (ev: StreamEvent) => {
      if (ev.phase === "extracting") {
        onProgress({ phase: "extracting", fileName });
      } else if (ev.phase === "processing") {
        onProgress({
          phase: "processing",
          current: ev.current ?? 0,
          total: ev.total ?? 0,
          fileName,
          skipped: ev.skipped,
        });
      } else if (ev.phase === "complete") {
        finishComplete(ev);
      } else if (ev.phase === "error") {
        finishError(ev.error ?? "Upload failed");
      }
    };

    const drainDownloadStream = () => {
      const text = xhr.responseText;
      const chunk = text.slice(responseOffset);
      responseOffset = text.length;
      if (!chunk) return;
      lineRemainder += chunk;
      lineRemainder = parseNdjsonChunk(lineRemainder, handleStreamEvent);
    };

    xhr.upload.addEventListener("progress", (e) => {
      if (!e.lengthComputable) return;
      const percent = Math.round((e.loaded / e.total) * 100);
      if (percent !== lastUploadPercent) {
        lastUploadPercent = percent;
        onProgress({ phase: "uploading", percent, fileName });
      }
    });

    xhr.addEventListener("progress", drainDownloadStream);

    xhr.addEventListener("load", () => {
      drainDownloadStream();

      if (!settled && xhr.status >= 200 && xhr.status < 300) {
        // Fallback: some browsers deliver the final NDJSON line only on load.
        parseNdjsonChunk(`${xhr.responseText}\n`, (ev) => {
          if (ev.phase === "complete") finishComplete(ev);
          else if (ev.phase === "error") finishError(ev.error ?? "Upload failed");
        });
      }

      if (settled) return;

      // Zip uploads always stream NDJSON with HTTP 200.
      if (xhr.status >= 200 && xhr.status < 300) {
        finishError("Upload finished without a completion event");
        return;
      }

      let message = `Upload failed (${xhr.status})`;
      const text = xhr.responseText.trim();
      if (text) {
        try {
          const firstLine = text.split("\n").find((l) => l.trim())?.trim() ?? text;
          const body = JSON.parse(firstLine) as { error?: string; phase?: string };
          if (body.error) message = body.error;
          else if (body.phase === "error" && (body as { error?: string }).error) {
            message = (body as { error: string }).error;
          }
        } catch {
          try {
            const body = JSON.parse(text) as { error?: string; ok?: boolean };
            if (body.error) message = body.error;
          } catch {
            // keep default message
          }
        }
      }
      finishError(message);
    });

    xhr.addEventListener("error", () => {
      finishError("Network error during upload");
    });

    xhr.addEventListener("abort", () => {
      if (!settled) {
        settled = true;
        reject(new ApiError("Upload cancelled", 0));
      }
    });

    xhr.send(formData);
  });
}

export function adminUploadGalleryZipWithProgress(
  albumId: number,
  file: File,
  onProgress: (event: GalleryUploadProgress) => void,
): Promise<GalleryUploadProgress & { phase: "complete" }> {
  const fd = new FormData();
  fd.append("zip", file);
  return xhrUploadWithStream(
    `/api/admin/gallery/albums/${albumId}/photos/upload-zip?stream=1`,
    fd,
    file.name,
    onProgress,
  );
}

export function adminUploadGalleryPhotosWithProgress(
  albumId: number,
  files: File[],
  onProgress: (event: GalleryUploadProgress) => void,
): Promise<GalleryUploadProgress & { phase: "complete" }> {
  const fd = new FormData();
  for (const file of files) {
    fd.append("files", file);
  }
  const label =
    files.length === 1 ? files[0]!.name : `${files.length} images`;
  return xhrUploadWithStream(
    `/api/admin/gallery/albums/${albumId}/photos/upload?stream=1`,
    fd,
    label,
    onProgress,
  );
}
